import { useQuery } from "@tanstack/react-query";
import { addDays, addMonths, endOfMonth, startOfDay, startOfMonth, subMonths } from "date-fns";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, queryString } from "../../api/client";
import { useVenueScope } from "../../app/venueScope";
import { formatDateTime, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  type Column,
  DataTable,
  EmptyState,
  ErrorPanel,
  Field,
  PageHeader,
  StatusPill,
} from "../../ui/components";
import { ListPageSkeleton } from "../../ui/skeletons";
import type { CalendarEntry, CalendarResponse } from "./calendarTypes";

// The calendar answers "what is happening on this date". This page answers the other
// half of the same question, "what is let out at all", which a grid cannot show: a
// calendar has no column to sort by and no way to compare thirty bookings at once.
// Both read the same /api/v1/calendar endpoint, so the two views can never disagree.
// It asks for kind=booking, because the client's own programme is a list of its own
// now and mixing the two would sit somebody else's hire beside an internal rehearsal.

const RANGE_OPTIONS = [
  { value: "upcoming", label: "Next 90 days" },
  { value: "month", label: "This month" },
  { value: "year", label: "Next 12 months" },
  { value: "past", label: "Past 12 months" },
] as const;

type RangeKey = (typeof RANGE_OPTIONS)[number]["value"];

// Deliberately the same vocabularies the calendar filters use. Two lists that drift
// apart would let a filter exist on one screen and not the other.
const STATUSES = [
  "draft",
  "enquiry",
  "held",
  "provisional",
  "pending_approval",
  "confirmed",
  "in_progress",
  "completed",
  "cancelled",
];

const PAYMENT_STATES = ["none", "unpaid", "part_paid", "paid", "overdue"];

function rangeFor(key: RangeKey): { from: Date; to: Date } {
  const today = startOfDay(new Date());
  if (key === "month") return { from: startOfMonth(today), to: endOfMonth(today) };
  if (key === "year") return { from: today, to: addMonths(today, 12) };
  if (key === "past") return { from: subMonths(today, 12), to: today };
  return { from: today, to: addDays(today, 90) };
}

export function BookingsPage() {
  const { selectedVenueId: venueId } = useVenueScope();
  const [range, setRange] = useState<RangeKey>("upcoming");
  const [status, setStatus] = useState("");
  const [payment, setPayment] = useState("");
  const [search, setSearch] = useState("");

  const bounds = useMemo(() => rangeFor(range), [range]);
  const query = queryString({
    from: bounds.from.toISOString(),
    to: bounds.to.toISOString(),
    kind: "booking",
    venueId,
    status,
    payment,
  });
  const bookings = useQuery({
    queryKey: ["bookings", "list", query],
    queryFn: () => api<CalendarResponse>(`/api/v1/calendar${query}`),
    // The venue scope resolves asynchronously; asking with an empty venue would answer
    // with every venue for a moment and then snap to one, which reads as a glitch.
    enabled: Boolean(venueId),
  });

  // Venue, status and payment are narrowed by the server so the row count is honest.
  // Free text stays on the client: it spans four fields at once and needs to feel
  // instant, and the server has no equivalent single-box search.
  const items = useMemo(() => {
    const all = bookings.data?.items ?? [];
    const needle = search.trim().toLowerCase();
    if (!needle) return all;
    return all.filter((item) =>
      [item.title, item.reference, item.customerName, item.venueNames.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [bookings.data, search]);

  const columns: Column<CalendarEntry>[] = [
    {
      label: "Booking",
      className: "cell-wrap",
      sortValue: (item) => item.title.toLowerCase(),
      render: (item) => <Link to={`/bookings/${item.bookingId}`}>{item.title}</Link>,
    },
    {
      label: "Reference",
      sortValue: (item) => item.reference,
      render: (item) => item.reference,
    },
    {
      label: "Customer",
      className: "cell-wrap",
      sortValue: (item) => item.customerName.toLowerCase(),
      render: (item) => item.customerName || "Not recorded",
    },
    {
      label: "Venue",
      className: "cell-wrap",
      sortValue: (item) => item.venueNames.join(", ").toLowerCase(),
      render: (item) => item.venueNames.join(", ") || "Not assigned",
    },
    {
      label: "Starts",
      // Sorts on the instant, not on the rendered string, so "1 Apr" cannot order
      // ahead of "2 Mar".
      sortValue: (item) => Date.parse(item.start),
      render: (item) => formatDateTime(item.start, item.timezone),
    },
    {
      label: "Status",
      sortValue: (item) => item.status,
      render: (item) => <StatusPill value={item.status} />,
    },
    {
      label: "Payment",
      sortValue: (item) => item.paymentState,
      render: (item) => <StatusPill value={item.paymentState} />,
    },
    {
      label: "Open tasks",
      className: "numeric",
      sortValue: (item) => item.openTaskCount,
      render: (item) =>
        item.overdueTaskCount > 0 ? (
          <span className="cell-attention">
            {item.openTaskCount} ({item.overdueTaskCount} overdue)
          </span>
        ) : (
          item.openTaskCount
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Bookings"
        description="Every rental on the books in one list: who has the space, when, and what they still owe."
      />
      <Card>
        <div className="filters">
          <Field label="Search">
            <input
              type="search"
              value={search}
              placeholder="Booking, reference, customer or venue"
              onChange={(event) => setSearch(event.target.value)}
            />
          </Field>
          <Field label="Date range">
            <select value={range} onChange={(event) => setRange(event.target.value as RangeKey)}>
              {RANGE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Booking status">
            <select value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">All statuses</option>
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Payment">
            <select value={payment} onChange={(event) => setPayment(event.target.value)}>
              <option value="">All payment states</option>
              {PAYMENT_STATES.map((value) => (
                <option key={value} value={value}>
                  {titleCase(value)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Card>
      {bookings.isPending ? (
        <ListPageSkeleton title="" description="" filterCount={0} columnCount={7} rowCount={8} />
      ) : null}
      {bookings.error ? <ErrorPanel error={bookings.error} /> : null}
      {bookings.data && items.length === 0 ? (
        <EmptyState
          title="No bookings match these filters"
          description="Widen the date range or clear a filter. Bookings outside the selected window are not loaded."
        />
      ) : null}
      {bookings.data && items.length > 0 ? (
        <Card>
          <CardHeader
            title="Booking list"
            description={`${items.length} ${items.length === 1 ? "booking" : "bookings"} in this range`}
          />
          <DataTable
            caption="Bookings matching the current filters"
            columns={columns}
            items={items}
            getKey={(item) => item.occurrenceId}
            defaultSort={{ label: "Starts", direction: "ascending" }}
          />
        </Card>
      ) : null}
    </>
  );
}
